<template>
    <div>
        <div class="card mb-3" :class="!savedCert.contentNumber ? 'border-danger' : isExpired ? 'border-warning' : 'border-success'">
            <div
                class="card-header text-white d-flex align-items-center justify-content-between"
                :class="!savedCert.contentNumber ? 'bg-danger' : isExpired ? 'bg-warning' : 'bg-success'"
            >
                <span>
                    <i class="fa-solid fa-certificate"></i>
                    B-BBEE Certificate:
                    {{ !savedCert.contentNumber
                        ? 'Please provide a valid certificate'
                        : isExpired
                            ? 'Expired - ' + savedCert.fileName
                            : 'Valid until ' + savedCert.expirationDate.split(" ")[0]
                    }}
                </span>
                <button
                    v-if="hasCertificate"
                    class="btn btn-sm btn-light ml-3"
                    type="button"
                    :disabled="isSaving"
                    @click="downloadCertificate"
                >
                    <i class="fa fa-download mr-1"></i>
                    Download
                </button>
            </div>
        </div>
        <div class="form-row row-spacing">
            <div class="form-group col-md-6">
                <label>File Upload</label>
                <input
                    type="file"
                    class="form-control"
                    :disabled="hasCertificate"
                    @change="fileSelected"
                />
            </div>
            <div class="form-group col-md-6">
                <label>Expiration Date</label>
                <input
                    v-model="expirationDateInput"
                    type="date"
                    class="form-control"
                    :disabled="hasCertificate"
                />
            </div>
        </div>
        <slot></slot>
    </div>
</template>

<script>
export default {
    props: {
        supplierNumber: {
            required: true,
            type: [Number, String]
        }
    },
    data() {
        return {
            savedCert: {
                supplierDocumentNumber: 0,
                fileName: '',
                description: '',
                contentNumber: 0,
                expirationDate: '1970-01-01'
            },
            certificate: {
                supplierDocumentNumber: 0,
                fileName: '',
                description: '',
                contentNumber: 0,
                expirationDate: '1970-01-01'
            },
            validationErrors: {},
            selectedFile: null,
            isSaving: false,
        }
    },
    mounted() {
        this.getCertificate();
    },
    computed: {
        expirationDateInput: {
            get() {
                if (!this.certificate.expirationDate) {
                    return "";
                }

                const normalizedDate = this.certificate.expirationDate.split(" ")[0];
                return normalizedDate === "1970-01-01" ? "" : normalizedDate;
            },
            set(value) {
                this.certificate.expirationDate = value && value.length > 0 ? value : "1970-01-01";
            },
        },
        isExpired() {
            if (!this.savedCert.expirationDate) return false; 
            
            const expDate = new Date(this.savedCert.expirationDate);
            return expDate < new Date(); 
        },
        hasExpirationDate() {
            return this.expirationDateInput.length > 0;
        },
        hasCertificate() {
            return !!this.savedCert.contentNumber;
        },
    },
    methods: {
        downloadCertificate() {
            this.$http.get(
                this.$config.aimsAPIv3
                    + "suppliers/"
                    + this.supplierNumber
                    + "/documents/"
                    + this.savedCert.supplierDocumentNumber
                    + "/download",
                { responseType: "arraybuffer" }
            ).then(
                (response) => {
                    const contentType = response.headers.get("Content-Type");
                    const blob = new Blob([response.data], { type: contentType });
                    const objectUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = objectUrl;
                    link.download = this.savedCert.fileName;
                    link.click();
                    window.URL.revokeObjectURL(objectUrl);
                },
                (error) => {
                    console.error(error);
                    this.showError("Error Downloading B-BBEE Certificate", error);
                }
            );
        },
        getCertificate() {
            this.$http.get(this.$config.aimsAPIv3 + "suppliers/" + this.supplierNumber + "/certificate")
                .then((response) => {
                    if(response.data) {
                        this.certificate = response.data;
                        this.savedCert = this.deepCopyObject(this.certificate);
                        this.$emit('certificate-changed', true);
                    } else {
                        this.resetCertificate();
                    }
                })
                .catch((error) => { 
                    console.error(error);
                    this.showError("Error Fetching Supplier B-BBEE Details", error);
                });
        },
        async saveCertificate() {
            if (!this.selectedFile && !this.certificate.contentNumber) {
                return true;
            }

            if (!this.hasExpirationDate) {
                this.showWarningAlert("Please select an expiration date.");
                return false;
            }

            this.isSaving = true;
            try {
                if (this.selectedFile) {
                    await this.uploadCertificate();
                } else {
                    await this.updateCertificate();
                }
                return true;
            } finally {
                this.isSaving = false;
            }
        },
        async updateCertificate() {
            const response = await this.$http.put(
                this.$config.aimsAPIv3 + "suppliers/update/certificate",
                this.certificate
            );
            this.certificate = response.data || this.certificate;
            this.savedCert = this.deepCopyObject(this.certificate);
        },
        async uploadCertificate() {
            const formData = new FormData();
            
            formData.append('file', this.selectedFile);
            const fileMetaData = [
                {
                    fileName: this.selectedFile.name,
                    description: this.certificate.description || 'B-BBEE Certificate',
                    expirationDate: this.certificate.expirationDate || '1970-01-01'
                }
            ];
            formData.append('data', new Blob([JSON.stringify(fileMetaData)], {
                type: 'application/json'
            }));

            const response = await this.$http.post(this.$config.aimsAPIv3 + 'suppliers/' + this.supplierNumber + '/certificate/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data' 
                }
            });

            this.certificate = response.data;
            this.savedCert = this.deepCopyObject(this.certificate);
            this.selectedFile = null;
            this.$emit('certificate-changed', true);
            this.$emit('documentUploaded');
        },
        deleteCertificate() {
            this.$swal({
                title: 'Are you sure you want to delete this B-BBEE certificate?',
                text: this.savedCert.fileName,
                type: 'warning',
                showCancelButton: true,
                cancelButtonColor: '#d33',
                confirmButtonText: 'Delete',
            }).then(async (result) => {
                if (!result.value) {
                    return;
                }

                this.isSaving = true;
                try {
                    await this.$http.delete(
                        this.$config.aimsAPIv3 + 'suppliers/' + this.supplierNumber + '/certificate'
                    );
                    this.resetCertificate();
                    this.showSuccess('B-BBEE certificate deleted');
                    this.$emit('documentUploaded');
                } catch (error) {
                    console.error(error);
                    this.showError('Error Deleting B-BBEE Certificate', error);
                } finally {
                    this.isSaving = false;
                }
            });
        },
        resetCertificate() {
            this.certificate = {
                supplierDocumentNumber: 0,
                fileName: '',
                description: '',
                contentNumber: 0,
                expirationDate: '1970-01-01'
            };
            this.savedCert = this.deepCopyObject(this.certificate);
            this.selectedFile = null;
            this.$emit('certificate-changed', false);
        },
        fileSelected(event) {
            this.selectedFile = event.target.files[0];
        }
    }
}
</script>

<style scoped>
.bg-success {
    background-color: #27c24c !important;
}

</style>